import * as THREE from 'three';
import { framePos, movePoint, poseBone, squashAt, type AnimParams, type ClipInfo } from '../anim/atlas.ts';
import { boiled, boilRuns, canBoil } from '../anim/boil.ts';
import type { BakedFrame, BakedGlow, BakedModel, BakedPart } from './bake.ts';
import { GlowLayer } from './glow.ts';

/**
 * The per-instance data of one frame (or one dynamic part). Every mesh of the frame, one per material,
 * reads the same attributes: each instance is written once, not once per material.
 */
interface Slot {
  meshes: THREE.InstancedMesh[];
  count: number;
  /** Lowest instance written since the last end() (Infinity: none): only from there on goes to the GPU. */
  dirty: number;
  mat: THREE.InstancedBufferAttribute;
  col: THREE.InstancedBufferAttribute;
  /** Rigged models: what each instance plays (see anim/skin.ts). */
  animA: THREE.InstancedBufferAttribute | null;
  animB: THREE.InstancedBufferAttribute | null;
}

/**
 * Draws every instance of a baked model with one InstancedMesh per (frame, material). A rigged nightmare's boil
 * frames draw together instead (anim/boil.ts): one InstancedMesh per material, the instances of each frame laid
 * one after the other at end(), in the same order as before.
 * Usage per render tick: begin() → add(...) for each entity → end().
 */
export class ModelBatch {
  readonly group = new THREE.Group();
  private frames: Slot[] = [];
  /** Rigged models whose boil frames draw together: the one slot that draws (the frames only collect). */
  private boil: Slot | null = null;
  private readonly boils: boolean;
  private dyn = new Map<string, Slot & { base: THREE.Matrix4 }>();
  private cap: number;
  private tmp = new THREE.Matrix4();
  private tmp2 = new THREE.Matrix4();
  private sq = new THREE.Vector3();
  private gv = new THREE.Vector3();
  private glowTmp: BakedGlow = { pos: new THREE.Vector3(), color: new THREE.Color(), size: 0, opacity: 0, bone: 0 };
  private white = new THREE.Color(1, 1, 1);
  /** Bone transforms of the instance being added, one per bone its glows ride (computed once each). */
  private bt = new Float32Array(8 * 64);
  private btViews = Array.from({ length: 64 }, (_, i) => this.bt.subarray(i * 8, i * 8 + 8));
  private btDone = new Int32Array(64);
  private btStamp = 0;

  constructor(
    readonly baked: BakedModel,
    private glow: GlowLayer | null,
    initialCap = 16,
    /** Only these parts get meshes here (the rest is drawn elsewhere: see structbatch.ts); glows always. */
    private keep: (p: BakedPart) => boolean = () => true,
  ) {
    this.cap = initialCap;
    this.group.name = `batch:${baked.key}`;
    this.group.matrixAutoUpdate = false;
    this.boils = baked.anim !== null && canBoil(baked.frames);
    this.build();
  }

  /** `draws` false: a boil frame that only collects its instances (no meshes). `cap`: room for that many. */
  private makeSlot(frame: BakedFrame, skinned: boolean, draws = true, cap = this.cap, boil = false): Slot {
    const mat = new THREE.InstancedBufferAttribute(new Float32Array(cap * 16), 16).setUsage(THREE.DynamicDrawUsage);
    const col = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3).setUsage(THREE.DynamicDrawUsage);
    const animA = skinned ? new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage) : null;
    const animB = skinned ? new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage) : null;
    const meshes = (draws ? frame.parts : []).flatMap((p, i) => {
      if (!this.keep(p)) return [];
      let geo = p.geometry;
      if (animA && animB) {
        // A view of the shared geometry with this batch's per-instance clips on top (vertex buffers stay shared).
        geo = new THREE.BufferGeometry();
        for (const [k, a] of Object.entries(p.geometry.attributes)) geo.setAttribute(k, a);
        // A far version draws its vertices through an index (engine/lod.ts).
        geo.setIndex(p.geometry.index);
        geo.boundingSphere = p.geometry.boundingSphere;
        geo.setAttribute('aAnimA', animA);
        geo.setAttribute('aAnimB', animB);
        if (boil) geo.setAttribute('aBoil', boilRuns(this.baked.frames, i));
      }
      const m = new THREE.InstancedMesh(geo, boil ? boiled(p.material) : p.material, cap);
      m.instanceMatrix = mat;
      m.instanceColor = col;
      m.count = 0;
      // Nothing to draw until end() says otherwise (a batch baked ahead of time would draw zero instances).
      m.visible = false;
      m.frustumCulled = false;
      m.castShadow = false;
      m.receiveShadow = false;
      // Where each instance stands is in its matrix: the mesh itself never moves.
      m.matrixAutoUpdate = false;
      this.group.add(m);
      return [m];
    });
    return { meshes, count: 0, dirty: 0, mat, col, animA, animB };
  }

  private build(): void {
    for (const f of this.frames) for (const m of f.meshes) this.dispose(m);
    for (const m of this.boil?.meshes ?? []) this.dispose(m);
    for (const d of this.dyn.values()) for (const m of d.meshes) this.dispose(m);
    const skinned = this.baked.anim !== null;
    this.frames = this.baked.frames.map((f) => this.makeSlot(f, skinned, !this.boils));
    const f0 = this.baked.frames[0];
    this.boil = this.boils && f0 ? this.makeSlot(f0, true, true, this.cap * this.baked.frames.length, true) : null;
    this.dyn.clear();
    for (const d of this.baked.dynamic) this.dyn.set(d.name, { base: d.base, ...this.makeSlot(d.frame, false) });
  }

  private dispose(m: THREE.InstancedMesh): void {
    this.group.remove(m);
    m.dispose();
  }

  private grow(): void {
    const copy = (s: Slot): { count: number; arrs: (Float32Array | null)[] } => ({
      count: s.count,
      arrs: [s.mat, s.col, s.animA, s.animB].map((a) => (a ? (a.array as Float32Array).slice() : null)),
    });
    const restore = (s: Slot, o: { count: number; arrs: (Float32Array | null)[] }): void => {
      s.count = o.count;
      [s.mat, s.col, s.animA, s.animB].forEach((a, i) => {
        const src = o.arrs[i];
        if (a && src) (a.array as Float32Array).set(src);
      });
    };
    const old = this.frames.map(copy);
    const oldDyn = [...this.dyn.entries()].map(([k, d]) => [k, copy(d)] as const);
    this.cap *= 2;
    this.build();
    this.frames.forEach((f, i) => restore(f, old[i] as ReturnType<typeof copy>));
    for (const [k, o] of oldDyn) {
      const d = this.dyn.get(k);
      if (d) restore(d, o);
    }
  }

  begin(): void {
    for (const f of this.frames) f.count = 0;
    for (const d of this.dyn.values()) d.count = 0;
  }

  /** A clip of this model's rig by name (null when it isn't rigged or lacks the clip). */
  clip(name: string): ClipInfo | null {
    return this.baked.anim?.clips.get(name) ?? null;
  }

  /**
   * Adds an instance. `color` tints (multiplies) the materials; values > 1 brighten (hit flash).
   * Rigged models take `anim` (what the instance plays; at rest without it).
   */
  add(matrix: THREE.Matrix4, frame: number, color?: THREE.Color, opacityGlow = 1, anim?: AnimParams): void {
    const fi = ((frame % this.frames.length) + this.frames.length) % this.frames.length;
    if ((this.frames[fi] as Slot).count >= this.cap) this.grow();
    const f = this.frames[fi] as Slot;
    const i = f.count++;
    if (i < f.dirty) f.dirty = i;
    let m = matrix;
    if (anim && (anim.a.squash || anim.b?.squash)) {
      squashAt(anim, this.sq);
      m = this.tmp.copy(matrix).scale(this.sq);
    }
    m.toArray(f.mat.array, i * 16);
    (color ?? this.white).toArray(f.col.array, i * 3);
    const set = this.baked.anim;
    if (set && f.animA && f.animB) {
      const a = anim?.a ?? set.rest;
      const b = anim?.b ?? null;
      const A = f.animA.array as Float32Array;
      const B = f.animB.array as Float32Array;
      A[i * 4] = a.base;
      A[i * 4 + 1] = a.frames;
      A[i * 4 + 2] = anim ? framePos(a, anim.ta) : 0;
      A[i * 4 + 3] = b && anim ? anim.w : 0;
      B[i * 4] = b ? b.base : a.base;
      B[i * 4 + 1] = b ? b.frames : a.frames;
      B[i * 4 + 2] = b && anim ? framePos(b, anim.tb) : 0;
      B[i * 4 + 3] = set.bones;
    }
    if (this.glow) {
      const baked = this.baked.frames[fi] as BakedFrame;
      this.btStamp++;
      for (const g of baked.glows) {
        if (anim && set && g.bone < 64) {
          // The glow rides its bone: placed where the clip puts it this frame (each bone worked out once).
          const view = this.btViews[g.bone] as Float32Array;
          if (this.btDone[g.bone] !== this.btStamp) {
            poseBone(anim, g.bone, view);
            this.btDone[g.bone] = this.btStamp;
          }
          const t = this.glowTmp;
          t.pos.copy(movePoint(view, this.gv.copy(g.pos)));
          t.color = g.color;
          t.size = g.size;
          t.opacity = g.opacity;
          this.glow.addLocal(m, t, opacityGlow);
        } else this.glow.addLocal(m, g, opacityGlow);
      }
    }
  }

  /** Adds a dynamic sub-part instance: entity matrix × pivot base × local animation. */
  addDynamic(name: string, matrix: THREE.Matrix4, local: THREE.Matrix4, color?: THREE.Color): void {
    if (!this.dyn.has(name)) return;
    if ((this.dyn.get(name) as Slot).count >= this.cap) this.grow();
    const d = this.dyn.get(name) as Slot & { base: THREE.Matrix4 };
    const i = d.count++;
    if (i < d.dirty) d.dirty = i;
    this.tmp.multiplyMatrices(matrix, d.base);
    this.tmp2.multiplyMatrices(this.tmp, local);
    this.tmp2.toArray(d.mat.array, i * 16);
    (color ?? this.white).toArray(d.col.array, i * 3);
    if (this.glow) {
      const baked = this.baked.dynamic.find((x) => x.name === name);
      if (baked) for (const g of baked.frame.glows) this.glow.addLocal(this.tmp2, g, 1);
    }
  }

  hasDynamic(name: string): boolean {
    return this.dyn.has(name);
  }

  end(): void {
    const flush = (s: Slot): void => {
      for (const m of s.meshes) {
        m.count = s.count;
        m.visible = s.count > 0;
      }
      const from = s.dirty;
      s.dirty = Infinity;
      if (from < s.count) {
        const n = s.count - from;
        s.mat.addUpdateRange(from * 16, n * 16);
        s.mat.needsUpdate = true;
        s.col.addUpdateRange(from * 3, n * 3);
        s.col.needsUpdate = true;
        if (s.animA && s.animB) {
          s.animA.addUpdateRange(from * 4, n * 4);
          s.animA.needsUpdate = true;
          s.animB.addUpdateRange(from * 4, n * 4);
          s.animB.needsUpdate = true;
        }
      }
    };
    if (this.boil) this.gatherBoil(this.boil);
    else for (const f of this.frames) flush(f);
    for (const d of this.dyn.values()) flush(d);
  }

  /** The boil frames' instances into the slot that draws, frame after frame, each tagged with its frame. */
  private gatherBoil(b: Slot): void {
    let n = 0;
    this.frames.forEach((f, fi) => {
      const k = f.count;
      if (!k) return;
      (b.mat.array as Float32Array).set((f.mat.array as Float32Array).subarray(0, k * 16), n * 16);
      (b.col.array as Float32Array).set((f.col.array as Float32Array).subarray(0, k * 3), n * 3);
      const A = b.animA?.array as Float32Array;
      const B = b.animB?.array as Float32Array;
      A.set((f.animA?.array as Float32Array).subarray(0, k * 4), n * 4);
      B.set((f.animB?.array as Float32Array).subarray(0, k * 4), n * 4);
      for (let i = n; i < n + k; i++) B[i * 4 + 3] = (B[i * 4 + 3] as number) + fi * 256;
      n += k;
    });
    b.count = n;
    for (const m of b.meshes) {
      m.count = n;
      m.visible = n > 0;
    }
    if (n > 0) {
      for (const [a, size] of [[b.mat, 16], [b.col, 3], [b.animA, 4], [b.animB, 4]] as const) {
        if (!a) continue;
        a.addUpdateRange(0, n * size);
        a.needsUpdate = true;
      }
    }
  }

  /**
   * Draws one empty instance (a zero matrix: no pixels) in every mesh that has nothing to draw, so the GPU gets
   * the vertex buffers and the driver its draw state before the model first shows up. Baked and compiled ahead,
   * a pack's first drawing was still a stall: 0.2 s on a desktop GPU, 0.8 s on an Intel HD 4600 under ANGLE
   * (Direct3D), seconds in a software renderer. cool() puts them back.
   */
  warm(): void {
    for (const s of this.slots()) {
      if (s.count > 0 || !s.meshes.length) continue;
      (s.mat.array as Float32Array).fill(0, 0, 16);
      // A rigged one at rest, as add() writes it without a clip (a frame count of zero would read no pose).
      const set = this.baked.anim;
      if (set && s.animA && s.animB) {
        (s.animA.array as Float32Array).set([set.rest.base, set.rest.frames, 0, 0]);
        (s.animB.array as Float32Array).set([set.rest.base, set.rest.frames, 0, set.bones]);
      }
      for (const a of [s.mat, s.animA, s.animB]) {
        if (!a) continue;
        a.addUpdateRange(0, a.itemSize);
        a.needsUpdate = true;
      }
      for (const m of s.meshes) {
        m.count = 1;
        m.visible = true;
      }
    }
  }

  cool(): void {
    for (const s of this.slots()) {
      if (s.count > 0) continue;
      for (const m of s.meshes) {
        m.count = 0;
        m.visible = false;
      }
    }
  }

  private slots(): Slot[] {
    return [...(this.boil ? [this.boil] : this.frames), ...this.dyn.values()];
  }

  /** Number of draw calls this batch issues right now. */
  drawCalls(): number {
    let n = 0;
    if (this.boil) n += this.boil.count > 0 ? this.boil.meshes.length : 0;
    else for (const f of this.frames) if (f.count > 0) n += f.meshes.length;
    for (const d of this.dyn.values()) if (d.count > 0) n += d.meshes.length;
    return n;
  }
}
